import React from 'react';
import { Replace, FileText } from 'lucide-react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { BulkAction } from './TableActionBar';
import TraceAllButton from './trace/TraceAllButton';
import { openMasterprosSearch, openProSearch } from '../utils/proSearch';

/**
 * Shared builders for the standard PRO mass actions that live in a TanTable's
 * action bar (Change All, Change Template for All, Trace All). Search Results,
 * the LC Dashboard, and the Dispatch Dashboard all render the same three from
 * here — the host only supplies its own modal/drawer openers and the permission
 * flag. Bucket-specific actions (Send All Haz Docs, Send All Rail Bill, Clear
 * All Leg Appt Dates, …) are passed through `extra`.
 *
 * Every action receives the table's CURRENTLY-FILTERED row originals at click
 * time, so scope is always "the PROs visible in this table", never the page.
 */

const proNum = (r: any): string => String(r?.pronumber ?? '');
const isTemplate = (p: string) => p.startsWith('T');

/** Non-template pronumbers (as strings) from a set of filtered row originals.
 *  Templates (T-prefixed) are dropped — Change All / Change Template never
 *  apply to templates, matching the legacy page-header behavior. */
export function prosFromRows(rows: any[]): string[] {
  return Array.from(new Set(rows.map(proNum).filter((p) => p && !isTemplate(p))));
}

/** TEMPLATE pronumbers (as strings) from a set of filtered row originals — the exact
 *  complement of prosFromRows.
 *
 *  Deduped for the same reason: the Line Items view is one row per invoice line, so a
 *  template with eight lines is eight rows, and a fan-out target set must name it once.
 */
export function templatesFromRows(rows: any[]): string[] {
  return Array.from(new Set(rows.map(proNum).filter((p) => p && isTemplate(p))));
}

/** Deduped MASTER PRO numbers (as strings) from a set of filtered row originals.
 *
 *  Only the accounting drills carry this column as the row's identity; everywhere else
 *  `masterpro` is a plain attribute of the order and this is not the right handle.
 */
export function masterprosFromRows(rows: any[]): string[] {
  return Array.from(
    new Set(rows.map((r) => String(r?.masterpro ?? '').trim()).filter(Boolean)),
  );
}

/** Numeric pronumbers from filtered row originals, for Trace All. */
export function proNumbersFromRows(rows: any[]): number[] {
  return rows.map((r) => Number(r?.pronumber)).filter((n) => Number.isFinite(n));
}

/** Deduped, strictly-positive numeric pronumbers, for the Documents view.
 *
 *  Stricter than proNumbersFromRows on two counts, both of which matter here:
 *  `> 0` is load-bearing on top of isFinite because Number('') and Number(null) are
 *  both 0, which would send a phantom "PRO 0" to the documents endpoint; and the
 *  dedupe keeps a row-expanding card (one row per invoice line item) from spending the
 *  request's PRO ceiling on the same pro dozens of times. Templates fall out for free
 *  — Number('T110') is NaN, and the documents endpoints take numeric pros only.
 *  Mirrors SearchResults' own allProNumbers derivation. */
export function documentProsFromRows(rows: any[]): number[] {
  const seen = new Set<number>();
  for (const r of rows) {
    const n = Number(r?.pronumber);
    if (Number.isFinite(n) && n > 0) seen.add(n);
  }
  return Array.from(seen);
}

// Match the page-header mass-action buttons exactly (blue = Change Template for
// All, amber = Change All) so the action bar reads the same as the cards header.
const CHANGE_TEMPLATE_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed';
const CHANGE_ALL_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed';
// Violet: the remaining bright slot. Change Template is blue, Change All amber, Trace
// All teal — the Documents switch has to read as a peer of those, not as the neutral
// pill TableActionBar falls back to.
const VIEW_DOCUMENTS_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed';
// Slate: a navigation, not a mutation. The three bright slots above all CHANGE orders;
// this one only opens another screen, and reading as their peer would overstate it.
const OPEN_SEARCH_CLS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed';

export interface ProBulkActionOpts {
  /**
   * From useCanUseChangeAll — gates Change All ALONE.
   *
   * Its own component (`change-all`) since the split: opening the bulk drawer and
   * editing LC order fields are different questions, and Dispatch/Scheduling need
   * the first (trouble-ticket fan-out) without the second. What the drawer then
   * offers is decided per arm — see useCanUseChangeAll.
   */
  canChangeAll: boolean;
  /**
   * From useCanEditOrderLcFields — gates Change Template for All, which stays on
   * the LC gate: applying a template rewrites the order's fields wholesale, so it
   * is a field edit in bulk however the button is labelled.
   */
  canChangeTemplate: boolean;
  onChangeAll: (pros: string[]) => void;
  onChangeTemplateAll: (pros: string[]) => void;
  /** Include the Trace All action (default true). */
  trace?: boolean;
  /**
   * Adds the Orders -> Documents switch. Given the deduped numeric PROs visible in
   * this table, which the host repaints its table with document results for.
   *
   * Opt-in rather than always-on for two reasons: Search Results already carries its
   * own page-level Documents toggle and would otherwise offer the switch twice, and
   * only a host that actually renders the repaint should advertise it.
   */
  onViewDocuments?: (pros: number[]) => void;
  /** Bucket-specific actions appended after the standard three. */
  extra?: BulkAction[];
}

export function buildProBulkActions(opts: ProBulkActionOpts): BulkAction[] {
  const actions: BulkAction[] = [];
  if (opts.canChangeAll) {
    actions.push({
      key: 'change-all',
      label: 'Change All',
      icon: <Replace className="h-4 w-4" />,
      className: CHANGE_ALL_CLS,
      onRun: (rows) => opts.onChangeAll(prosFromRows(rows)),
    });
  }
  if (opts.canChangeTemplate) {
    actions.push({
      key: 'change-template-all',
      label: 'Change Template for All',
      icon: <Replace className="h-4 w-4" />,
      className: CHANGE_TEMPLATE_CLS,
      onRun: (rows) => opts.onChangeTemplateAll(prosFromRows(rows)),
    });
  }
  if (opts.trace !== false) {
    actions.push({
      key: 'trace-all',
      render: (getFilteredRows) => (
        <TraceAllButton
          variant="text"
          getPronumbers={() => proNumbersFromRows(getFilteredRows())}
        />
      ),
    });
  }
  if (opts.onViewDocuments) {
    actions.push({
      key: 'view-documents',
      label: 'Documents',
      icon: <FileText className="h-4 w-4" />,
      className: VIEW_DOCUMENTS_CLS,
      title: 'Show every document attached to the PROs visible in this table',
      onRun: (rows) => opts.onViewDocuments!(documentProsFromRows(rows)),
    });
  }
  if (opts.extra && opts.extra.length) actions.push(...opts.extra);
  return actions;
}

export interface TemplateBulkActionOpts {
  /** From useCanEditTemplateFields — the single CSR/LC gate /pro puts every editable
   *  template field behind. Not order-lc-fields: editing templates in bulk must need
   *  the same grant as editing one. */
  canEdit: boolean;
  onChangeAll: (templates: string[]) => void;
  /** Bucket-specific actions appended after Change All. */
  extra?: BulkAction[];
}

/**
 * The Templates board's action bar. Change All only, and deliberately so:
 *
 *   Change Template for All  applies a template TO an order — meaningless when the
 *                            records in the table ARE templates.
 *   Trace All                traces a container on a live move; a template has no
 *                            container, no vessel and no move to trace.
 *   Documents                the documents endpoints take numeric pros.
 *
 * Same amber button and the same underlying engine as the PRO bar — the fan-out runs
 * through the same /orders/change-all pipeline, which routes to the template tables off
 * the 'T' pronumber.
 */
export function buildTemplateBulkActions(opts: TemplateBulkActionOpts): BulkAction[] {
  const actions: BulkAction[] = [];
  if (opts.canEdit) {
    actions.push({
      key: 'change-all',
      label: 'Change All',
      icon: <Replace className="h-4 w-4" />,
      className: CHANGE_ALL_CLS,
      title: 'Apply a field change to every template visible in this table',
      onRun: (rows) => opts.onChangeAll(templatesFromRows(rows)),
    });
  }
  if (opts.extra && opts.extra.length) actions.push(...opts.extra);
  return actions;
}

export interface AcctDrillBulkActionOpts {
  /** From useCanUseChangeAll. False hides Change All and leaves the search hop. */
  canChangeAll: boolean;
  /**
   * What a row in THIS drill is. The accounting drills are the only PRO tables in 360
   * whose row is sometimes not an order: the Pending Invoicing and Invoicing Hold
   * families draw one row per MASTERPRO over a representative pro
   * (accounting_dashboards.py::row_type_for).
   */
  rowsAre: 'orders' | 'masterpros';
  onChangeAll: (pros: string[]) => void;
}

/**
 * The Accounting drill's action bar. Narrower than the PRO bar on purpose:
 *
 *   Change Template for All  rewrites an order's fields off a template — an order-entry
 *                            act, not an invoicing one, and it needs the LC field gate
 *                            this audience does not hold.
 *   Trace All                traces a container in transit; these buckets are about
 *                            orders that have already delivered.
 *   Documents                the drill has no document repaint to switch to.
 *
 * Change All is offered only where a ROW IS AN ORDER. On the masterpro-grouped buckets
 * the row's `pronumber` is the one representative V1's cron stored, so fanning out over
 * it would quietly edit 1 of the N orders the row stands for — the failure would look
 * like a partial save with nothing to explain it. Those buckets get the search hop
 * instead, which expands the masterpros into their real order set in a tab where Change
 * All is honest about how many orders it is about to touch.
 */
export function buildAcctDrillBulkActions(opts: AcctDrillBulkActionOpts): BulkAction[] {
  const actions: BulkAction[] = [];
  if (opts.canChangeAll && opts.rowsAre === 'orders') {
    actions.push({
      key: 'change-all',
      label: 'Change All',
      icon: <Replace className="h-4 w-4" />,
      className: CHANGE_ALL_CLS,
      title: 'Apply a field change to every order visible in this table',
      onRun: (rows) => opts.onChangeAll(prosFromRows(rows)),
    });
  }
  actions.push({
    key: 'open-in-search',
    label: 'Open in Search Results',
    icon: <ArrowTopRightOnSquareIcon className="h-4 w-4" />,
    className: OPEN_SEARCH_CLS,
    title: opts.rowsAre === 'masterpros'
      ? 'Open every order on the Master PROs visible in this table, in a new tab'
      : 'Open the orders visible in this table in Search Results, in a new tab',
    onRun: (rows) => (
      opts.rowsAre === 'masterpros'
        ? openMasterprosSearch(masterprosFromRows(rows))
        : openProSearch(prosFromRows(rows))
    ),
  });
  return actions;
}
